import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BaseEntity, EntityType } from "@/types/entities";
import { checkSingleEntityDuplicate } from "@/utils/duplicateChecker";
import { normalizeState } from "@/utils/entityUpdateUtils";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { BRAZILIAN_STATES, BRAZILIAN_REGIONS, STATE_TO_REGION } from "@/utils/brazilData";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface AddEntityDialogProps<T extends BaseEntity> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (entity: Omit<T, 'id'>) => Promise<void>;
  entityType: EntityType;
  entityTitle: string;
}

export function AddEntityDialog<T extends BaseEntity>({
  open,
  onOpenChange,
  onAdd,
  entityType,
  entityTitle,
}: AddEntityDialogProps<T>) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [showDuplicateAlert, setShowDuplicateAlert] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    region: "",
    state: "",
    sector: "",
    registrationForm: false,
    form: false,
    logo: false,
    observations: "",
    instagram: "",
  });

  const resetForm = () => {
    setFormData({
      name: "",
      region: "",
      state: "",
      sector: "",
      registrationForm: false,
      form: false,
      logo: false,
      observations: "",
      instagram: "",
    });
  };

  const handleSubmit = async (forceAdd: boolean = false) => {
    // Validação básica
    if (!formData.name.trim() || !formData.state.trim()) {
      toast({
        title: "Campos obrigatórios",
        description: "Preencha Nome e Estado.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      // Verifica duplicação
      if (!forceAdd) {
        const isDuplicate = await checkSingleEntityDuplicate(
          formData.name,
          entityType,
          formData.state
        );

        if (isDuplicate) {
          setLoading(false);
          setShowDuplicateAlert(true);
          return;
        }
      }

      // Normaliza o estado antes de adicionar
      const normalizedData = {
        ...formData,
        state: formData.state ? normalizeState(formData.state) : formData.state
      };

      // Adiciona a entidade
      await onAdd(normalizedData as Omit<T, 'id'>);

      toast({
        title: `${entityTitle} adicionado`,
        description: `${entityTitle} foi criado com sucesso.`,
      });

      resetForm();
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error adding entity:", error);
      
      let errorMessage = `Não foi possível criar ${entityTitle.toLowerCase()}.`;
      
      // Erro de permissão RLS
      if (error.code === '42501') {
        errorMessage = "Você não tem permissão para criar empresas. Contate um administrador.";
      } 
      // Erro de duplicação
      else if (error.code === '23505') {
        errorMessage = "Já existe uma empresa com esse nome.";
      } 
      // Mostrar mensagem do erro
      else if (error.message) {
        errorMessage = `Erro: ${error.message}`;
      }
      
      toast({
        title: "Erro ao adicionar",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateExisting = async () => {
    setShowDuplicateAlert(false);
    await handleSubmit(true);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Adicionar {entityTitle}</DialogTitle>
            <DialogDescription>
              Preencha os dados da entidade. Campos com * são obrigatórios.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2 col-span-2">
              <Label htmlFor="name">Nome *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Nome da entidade"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="state">Estado *</Label>
              <Select
                value={formData.state}
                onValueChange={(value) => {
                  setFormData({
                    ...formData,
                    state: value,
                    region: STATE_TO_REGION[value] || formData.region,
                  });
                }}
              >
                <SelectTrigger className="bg-background">
                  <SelectValue placeholder="Selecione o estado" />
                </SelectTrigger>
                <SelectContent className="bg-background z-50">
                  {BRAZILIAN_STATES.map((state) => (
                    <SelectItem key={state.value} value={state.value}>
                      {state.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="region">Região</Label>
              <Select
                value={formData.region}
                onValueChange={(value) => setFormData({ ...formData, region: value })}
              >
                <SelectTrigger className="bg-background">
                  <SelectValue placeholder="Selecione a região" />
                </SelectTrigger>
                <SelectContent className="bg-background z-50">
                  {BRAZILIAN_REGIONS.map((region) => (
                    <SelectItem key={region} value={region}>
                      {region}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 col-span-2">
              <Label htmlFor="sector">Setor</Label>
              <Input
                id="sector"
                value={formData.sector}
                onChange={(e) => setFormData({ ...formData, sector: e.target.value })}
                placeholder="Ex: Fast Food, Varejo, etc."
              />
            </div>

            <div className="space-y-2 col-span-2">
              <Label htmlFor="instagram">Instagram</Label>
              <Input
                id="instagram"
                value={formData.instagram}
                onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                placeholder="@usuario"
              />
            </div>

            <div className="space-y-4 col-span-2 border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="registrationForm">Ficha de Cadastro</Label>
                <Switch
                  id="registrationForm"
                  checked={formData.registrationForm}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, registrationForm: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="form">Formulário</Label>
                <Switch
                  id="form"
                  checked={formData.form}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, form: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="logo">Logo</Label>
                <Switch
                  id="logo"
                  checked={formData.logo}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, logo: checked })
                  }
                />
              </div>
            </div>

            <div className="space-y-2 col-span-2">
              <Label htmlFor="observations">Observações</Label>
              <Textarea
                id="observations"
                value={formData.observations}
                onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
                placeholder="Observações adicionais..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                resetForm();
                onOpenChange(false);
              }}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button onClick={() => handleSubmit(false)} disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDuplicateAlert} onOpenChange={setShowDuplicateAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{entityTitle} já existe</AlertDialogTitle>
            <AlertDialogDescription>
              Já existe um(a) {entityTitle.toLowerCase()} com o nome "{formData.name}" no estado "{formData.state}".
              Deseja atualizar os dados existentes?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleUpdateExisting}>
              Atualizar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
